import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { FiPieChart, FiBarChart } from 'react-icons/fi';

const AttendanceChart = ({ data, isLoading, chartType = 'pie' }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        shadow="sm"
      >
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" />
        </Flex>
      </Box>
    );
  }

  // Use processed chart data directly
  const chartData = data?.chartData || [];
  
  // Calculate total for display
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (chartType === 'pie') {
    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        shadow="sm"
      >
        <HStack mb={4}>
          <FiPieChart size={20} color="#4A5568" />
          <Heading size="sm" color="gray.900" fontWeight="600">
            Attendance Distribution
          </Heading>
        </HStack>

        {/* Simple Pie Chart Visualization */}
        <Box position="relative" mb={6}>
          <Box
            width="200px"
            height="200px"
            borderRadius="50%"
            position="relative"
            margin="0 auto"
            background="conic-gradient(from 0deg, #48BB78 0deg, #48BB78 calc(3.6deg * var(--present)), #F56565 calc(3.6deg * var(--present)), #F56565 calc(3.6deg * (var(--present) + var(--absent))), #ED8936 calc(3.6deg * (var(--present) + var(--absent))), #ED8936 calc(3.6deg * (var(--present) + var(--absent) + var(--leave))), #9F7AEA calc(3.6deg * (var(--present) + var(--absent) + var(--leave))), #9F7AEA 360deg)"
            style={{
              '--present': chartData[0].percentage,
              '--absent': chartData[1].percentage,
              '--leave': chartData[2].percentage,
            }}
          />
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center"
          >
            <Text fontSize="2xl" fontWeight="700" color="gray.900">
              {total}
            </Text>
            <Text fontSize="sm" color="gray.600">
              Total
            </Text>
          </Box>
        </Box>

        {/* Legend */}
        <VStack spacing={3} align="start">
          {chartData.map((item, index) => (
            <HStack key={index} justify="space-between" w="full">
              <HStack spacing={2}>
                <Box
                  width="12px"
                  height="12px"
                  borderRadius="2px"
                  bg={item.color}
                />
                <Text fontSize="sm" color="gray.700" fontWeight="500">
                  {item.label}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  {item.value}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  ({item.percentage}%)
                </Text>
              </HStack>
            </HStack>
          ))}
        </VStack>
      </Box>
    );
  }

  if (chartType === 'bar') {
    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        shadow="sm"
      >
                 <HStack mb={4}>
           <FiBarChart size={20} color="#4A5568" />
           <Heading size="sm" color="gray.900" fontWeight="600">
             Attendance Comparison
           </Heading>
         </HStack>

        {/* Simple Bar Chart */}
        <VStack spacing={4} align="stretch">
          {chartData.map((item, index) => (
            <Box key={index}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.700" fontWeight="500">
                  {item.label}
                </Text>
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  {item.value} ({item.percentage}%)
                </Text>
              </HStack>
              <Box
                width="100%"
                height="8px"
                bg="gray.100"
                borderRadius="full"
                overflow="hidden"
              >
                <Box
                  height="100%"
                  width={`${item.percentage}%`}
                  bg={item.color}
                  borderRadius="full"
                  transition="width 0.3s ease"
                />
              </Box>
            </Box>
          ))}
        </VStack>

        {/* Summary */}
        <Box mt={4} p={3} bg="gray.50" borderRadius="md">
          <Text fontSize="sm" color="gray.600" textAlign="center">
            Total Employees: <strong>{total}</strong>
          </Text>
        </Box>
      </Box>
    );
  }

  return null;
};

export default AttendanceChart; 